import { useState, useEffect } from 'react';
import { Zap, Search, Play, Settings2, CircleCheck as CheckCircle2, Clock, TrendingUp, Package, Building2, Users, ChartBar as BarChart3, PlugZap, Star, Loader as Loader2, History, GitBranch, X, ChevronDown, ChevronUp, Trash2, Banknote, Upload } from 'lucide-react';
import type { SkillDefinition, CompanySkill, SkillCategory, SkillColor } from './types';
import { CATEGORY_LABELS, SCHEDULE_LABELS } from './types';
import { SkillActivationWizard } from './SkillActivationWizard';
import { WorkflowBuilder } from './WorkflowBuilder';
import { SkillExecutionHistory } from './SkillExecutionHistory';
import { supabase } from '@/lib/supabase';
import { useCompany } from '@/context/CompanyContext';
import { useAuth } from '@/features/auth';

type Tab = 'store' | 'my-skills' | 'workflows' | 'history';

const CATEGORY_ICONS: Record<SkillCategory, React.ReactNode> = {
  hr: <Users className="w-4 h-4" />,
  accounting: <Building2 className="w-4 h-4" />,
  inventory: <Package className="w-4 h-4" />,
  sales: <TrendingUp className="w-4 h-4" />,
  integrations: <PlugZap className="w-4 h-4" />,
  custom: <Star className="w-4 h-4" />,
};

const COLOR_BG: Record<SkillColor, string> = {
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  rose: 'bg-rose-50 text-rose-700 border-rose-200',
  slate: 'bg-slate-50 text-slate-700 border-slate-200',
  violet: 'bg-violet-50 text-violet-700 border-violet-200',
};

const COLOR_ICON_BG: Record<SkillColor, string> = {
  blue: 'bg-blue-100 text-blue-600',
  emerald: 'bg-emerald-100 text-emerald-600',
  amber: 'bg-amber-100 text-amber-600',
  rose: 'bg-rose-100 text-rose-600',
  slate: 'bg-slate-100 text-slate-600',
  violet: 'bg-violet-100 text-violet-600',
};

const SKILL_ICONS: Record<string, React.ReactNode> = {
  Clock: <Clock className="w-5 h-5" />,
  Building2: <Building2 className="w-5 h-5" />,
  Package: <Package className="w-5 h-5" />,
  TrendingUp: <TrendingUp className="w-5 h-5" />,
  UserPlus: <Users className="w-5 h-5" />,
  BarChart3: <BarChart3 className="w-5 h-5" />,
  Zap: <Zap className="w-5 h-5" />,
};

export function SkillsStorePage() {
  const { activeCompanyId } = useCompany();
  const { user: _user } = useAuth();

  const [tab, setTab] = useState<Tab>('store');
  const [skills, setSkills] = useState<SkillDefinition[]>([]);
  const [activeSkills, setActiveSkills] = useState<CompanySkill[]>([]);
  const [loadingSkills, setLoadingSkills] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<SkillCategory | 'all'>('all');

  const [activatingSkill, setActivatingSkill] = useState<SkillDefinition | null>(null);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [expandedActive, setExpandedActive] = useState<Record<string, boolean>>({});
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [pendingFile, setPendingFile] = useState<{ csId: string; filename: string; headers: string[]; rows: Record<string, unknown>[]; row_count: number } | null>(null);
  const [resultModal, setResultModal] = useState<{ title: string; content: string } | null>(null);

  useEffect(() => {
    loadData();
  }, [activeCompanyId]);

  const loadData = async () => {
    setLoadingSkills(true);
    const [{ data: defs }, { data: company }] = await Promise.all([
      supabase.from('skill_definitions').select('*').eq('is_published', true).order('sort_order'),
      activeCompanyId
        ? supabase.from('company_skills').select('*, skill_definitions(*)').eq('company_id', activeCompanyId).eq('is_active', true)
        : Promise.resolve({ data: [] }),
    ]);
    setSkills((defs as SkillDefinition[]) ?? []);
    setActiveSkills((company as CompanySkill[]) ?? []);
    setLoadingSkills(false);
  };

  const handleActivated = () => {
    setActivatingSkill(null);
    showSuccess('הסקיל הופעל בהצלחה!');
    loadData();
  };

  const handleDeactivate = async (csId: string) => {
    await supabase.from('company_skills').update({ is_active: false }).eq('id', csId);
    setActiveSkills(prev => prev.filter(cs => cs.id !== csId));
  };



  const handleRunSkill = async (csId: string, fileData?: { filename: string; headers: string[]; rows: Record<string, unknown>[]; row_count: number }) => {
    if (!activeCompanyId) return;
    setRunningId(csId);
    setPendingFile(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const payload: Record<string, unknown> = { company_skill_id: csId, company_id: activeCompanyId };
      if (fileData) payload.file_data = fileData;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/skill-orchestrator`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const json = await res.json();
      if (json.status === 'success') {
        const stepsLog = json.steps_log as { output?: { type?: string; analysis?: string } }[] | undefined;
        const fileAnalysis = stepsLog?.find(s => s.output?.type === 'file_analysis');
        if (fileAnalysis?.output?.analysis) {
          setResultModal({ title: json.output_summary ?? 'תוצאת ניתוח', content: fileAnalysis.output.analysis });
        } else {
          showSuccess(`הסקיל הסתיים: ${json.output_summary ?? 'בהצלחה'}`);
        }
      }
      else showSuccess(`שגיאה: ${json.error ?? 'הריצה נכשלה'}`, true);
      loadData();
    } finally {
      setRunningId(null);
    }
  };

  const handleFileSelect = async (csId: string, file: File) => {
    try {
      const { read, utils } = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const workbook = read(buffer, { type: 'array' });
      const allRows: Record<string, unknown>[] = [];
      let headers: string[] = [];
      for (const sheetName of workbook.SheetNames) {
        const sheet = workbook.Sheets[sheetName]!;
        const jsonData = utils.sheet_to_json<Record<string, unknown>>(sheet);
        if (jsonData.length === 0) continue;
        if (headers.length === 0) headers = Object.keys(jsonData[0]!);
        for (const row of jsonData) {
          allRows.push({ __sheet__: sheetName, ...row });
        }
      }
      if (allRows.length === 0) throw new Error('No data');
      if (!headers.includes('__sheet__')) headers = ['__sheet__', ...headers];
      setPendingFile({ csId, filename: file.name, headers, rows: allRows, row_count: allRows.length });
    } catch {
      showSuccess('שגיאה בקריאת הקובץ. ודא שזה קובץ אקסל תקין.', true);
    }
  };

  const handleRunWorkflow = async (workflowId: string) => {
    if (!activeCompanyId) return;
    setRunningId(workflowId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/skill-orchestrator`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ workflow_id: workflowId, company_id: activeCompanyId }),
        }
      );
      const json = await res.json();
      if (json.status === 'success') showSuccess(`הזרימה הסתיימה: ${json.output_summary ?? 'בהצלחה'}`);
      else showSuccess(`שגיאה: ${json.error ?? 'הריצה נכשלה'}`, true);
    } finally {
      setRunningId(null);
    }
  };

  const showSuccess = (msg: string, _isError = false) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 5000);
  };

  const categories = [...new Set(skills.map(s => s.category))] as SkillCategory[];
  const isActive = (skillId: string) => activeSkills.some(cs => cs.skill_id === skillId);

  const filtered = skills.filter(s => {
    const matchesSearch = !search || s.name_he.includes(search) || s.description_he.includes(search);
    const matchesCat = categoryFilter === 'all' || s.category === categoryFilter;
    return matchesSearch && matchesCat;
  });

  const grouped = categories.reduce<Record<string, SkillDefinition[]>>((acc, cat) => {
    const catSkills = filtered.filter(s => s.category === cat);
    if (catSkills.length > 0) acc[cat] = catSkills;
    return acc;
  }, {});

  const totalSaved = activeSkills.reduce((acc, cs) => acc + (cs.skill_definitions?.estimated_minutes_saved ?? 0), 0);
  const totalCostSaved = activeSkills.reduce((acc, cs) => acc + (cs.skill_definitions?.estimated_cost_saved ?? 0), 0);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" dir="rtl">

      {/* Toast */}
      {successMsg && (
        <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 max-w-sm">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <span className="flex-1">{successMsg}</span>
          <button onClick={() => setSuccessMsg(null)}><X className="w-3.5 h-3.5 opacity-60 hover:opacity-100" /></button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-sm">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">חנות הסקילים</h1>
            <p className="text-sm text-gray-500 mt-0.5">תהליכי עבודה אוטומטיים מבוססי AI לעסק שלך</p>
          </div>
        </div>
        {activeSkills.length > 0 && (
          <div className="flex gap-3 shrink-0">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-4 py-2 text-center">
              <p className="text-2xl font-bold text-emerald-700">{totalSaved}</p>
              <p className="text-xs text-emerald-600">דקות חיסכון/הרצה</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-2xl px-4 py-2 text-center">
              <p className="text-2xl font-bold text-blue-700">₪{totalCostSaved}</p>
              <p className="text-xs text-blue-600">חיסכון כסף/הרצה</p>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
        {([
          { id: 'store', label: 'חנות', icon: <Zap className="w-3.5 h-3.5" /> },
          { id: 'my-skills', label: `הסקילים שלי${activeSkills.length > 0 ? ` (${activeSkills.length})` : ''}`, icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
          { id: 'workflows', label: 'זרימות עבודה', icon: <GitBranch className="w-3.5 h-3.5" /> },
          { id: 'history', label: 'היסטוריה', icon: <History className="w-3.5 h-3.5" /> },
        ] as { id: Tab; label: string; icon: React.ReactNode }[]).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all
              ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {/* ── STORE TAB ─────────────────────────────────────────────── */}
      {tab === 'store' && (
        <div className="space-y-6">
          {/* Search + filter */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="חיפוש סקיל..."
                className="w-full pr-9 pl-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setCategoryFilter('all')}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${categoryFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                הכל
              </button>
              {categories.map(cat => (
                <button key={cat} onClick={() => setCategoryFilter(cat)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all
                    ${categoryFilter === cat ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                  {CATEGORY_ICONS[cat]}{CATEGORY_LABELS[cat]}
                </button>
              ))}
            </div>
          </div>

          {loadingSkills ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : (
            Object.entries(grouped).map(([cat, catSkills]) => (
              <div key={cat}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="text-gray-500">{CATEGORY_ICONS[cat as SkillCategory]}</div>
                  <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">{CATEGORY_LABELS[cat as SkillCategory]}</h2>
                  <div className="flex-1 h-px bg-gray-100" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {catSkills.map(skill => (
                    <SkillCard
                      key={skill.id}
                      skill={skill}
                      activated={isActive(skill.id)}
                      onActivate={() => setActivatingSkill(skill)}
                      onManage={() => { setCategoryFilter('all'); setTab('my-skills'); }}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── MY SKILLS TAB ────────────────────────────────────────── */}
      {tab === 'my-skills' && (
        <div className="space-y-4">
          {activeSkills.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Zap className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm">אין סקילים פעילים. עבור לחנות כדי להפעיל.</p>
              <button onClick={() => setTab('store')} className="mt-3 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors">
                עבור לחנות
              </button>
            </div>
          ) : (
            activeSkills.map(cs => {
              const skill = cs.skill_definitions;
              if (!skill) return null;
              const isExpanded = !!expandedActive[cs.id];
              return (
                <div key={cs.id} className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                  <div className="px-5 py-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${COLOR_ICON_BG[skill.color as SkillColor] ?? 'bg-blue-100 text-blue-600'}`}>
                      {SKILL_ICONS[skill.icon] ?? <Zap className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900">{cs.name_override ?? skill.name_he}</p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-gray-500">{SCHEDULE_LABELS[cs.schedule_type]}</span>
                        {cs.last_run_at && (
                          <span className="text-xs text-gray-400">
                            הרצה אחרונה: {new Date(cs.last_run_at).toLocaleDateString('he-IL')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => setExpandedActive(prev => ({ ...prev, [cs.id]: !prev[cs.id] }))}
                        className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleRunSkill(cs.id)} disabled={runningId === cs.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                        {runningId === cs.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                        הפעל
                      </button>
                      <button onClick={() => setActivatingSkill(skill)} className="p-1.5 text-gray-400 hover:text-blue-500 transition-colors">
                        <Settings2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeactivate(cs.id)} className="p-1.5 text-gray-300 hover:text-red-500 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-100 px-5 py-4 space-y-4">
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 mb-2">הגדרות</h4>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {Object.entries(cs.config).map(([k, v]) => (
                            <div key={k} className="bg-gray-50 rounded-lg px-3 py-2">
                              <span className="text-gray-500">{k}:</span>{' '}
                              <span className="font-medium text-gray-800">{String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Prompt status (read-only) */}
                      <div className="flex items-center gap-2">
                        <h4 className="text-xs font-semibold text-gray-500">הוראות ל-AI:</h4>
                        <p className="text-xs text-gray-400 italic">
                          {skill.custom_prompt ? 'הוראות מוגדרות על ידי מנהל המערכת' : 'הסקיל ישתמש ב-prompt אוטומטי'}
                        </p>
                      </div>

                      {/* File Upload */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 mb-2">העלאת קובץ לניתוח</h4>
                        {pendingFile?.csId === cs.id ? (
                          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <Upload className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-blue-800">{pendingFile.filename}</span>
                              <span className="text-xs text-blue-600">({pendingFile.row_count} שורות, {pendingFile.headers.length} עמודות)</span>
                            </div>
                            <p className="text-[11px] text-blue-600">עמודות: {pendingFile.headers.slice(0, 8).join(', ')}{pendingFile.headers.length > 8 ? '...' : ''}</p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleRunSkill(cs.id, pendingFile)}
                                disabled={runningId === cs.id}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                              >
                                {runningId === cs.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                                הפעל עם הקובץ
                              </button>
                              <button
                                onClick={() => setPendingFile(null)}
                                className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                              >
                                הסר קובץ
                              </button>
                            </div>
                          </div>
                        ) : (
                          <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                            <Upload className="w-4 h-4 text-gray-400" />
                            <span className="text-xs text-gray-500">בחר קובץ אקסל (.xlsx, .xls, .csv) לניתוח</span>
                            <input
                              type="file"
                              accept=".xlsx,.xls,.csv"
                              className="hidden"
                              onChange={e => {
                                const file = e.target.files?.[0];
                                if (file) handleFileSelect(cs.id, file);
                                e.target.value = '';
                              }}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── WORKFLOWS TAB ────────────────────────────────────────── */}
      {tab === 'workflows' && (
        <WorkflowBuilder
          activeSkills={activeSkills}
          onRunWorkflow={handleRunWorkflow}
          runningId={runningId}
        />
      )}

      {/* ── HISTORY TAB ──────────────────────────────────────────── */}
      {tab === 'history' && <SkillExecutionHistory />}

      {/* Activation wizard modal */}
      {activatingSkill && (
        <SkillActivationWizard
          skill={activatingSkill}
          onClose={() => setActivatingSkill(null)}
          onActivated={handleActivated}
        />
      )}

      {/* Result Modal */}
      {resultModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" dir="rtl">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
              <h2 className="text-lg font-bold text-gray-900">תוצאת ניתוח</h2>
              <button onClick={() => setResultModal(null)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 text-xs text-gray-500 border-b border-gray-50 shrink-0">
              {resultModal.title}
            </div>
            <div className="px-6 py-5 overflow-y-auto flex-1">
              <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                {resultModal.content}
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-100 flex justify-end shrink-0">
              <button
                onClick={() => setResultModal(null)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700"
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SkillCard({ skill, activated, onActivate, onManage }: {
  skill: SkillDefinition;
  activated: boolean;
  onActivate: () => void;
  onManage: () => void;
}) {
  const colorBg = COLOR_BG[skill.color as SkillColor] ?? 'bg-blue-50 text-blue-700 border-blue-200';
  const iconBg = COLOR_ICON_BG[skill.color as SkillColor] ?? 'bg-blue-100 text-blue-600';

  return (
    <div className={`group relative bg-white border rounded-2xl p-5 shadow-sm hover:shadow-md transition-all ${activated ? 'border-emerald-300 ring-1 ring-emerald-200' : 'border-gray-200'}`}>
      {activated && (
        <div className="absolute top-3 left-3 flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
          <CheckCircle2 className="w-3 h-3" />
          פעיל
        </div>
      )}

      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
          {SKILL_ICONS[skill.icon] ?? <Zap className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900 text-sm leading-snug">{skill.name_he}</h3>
          <span className={`inline-block text-xs px-2 py-0.5 rounded-full border mt-1 ${colorBg}`}>
            {CATEGORY_LABELS[skill.category]}
          </span>
        </div>
      </div>

      <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-3">{skill.description_he}</p>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <Clock className="w-3.5 h-3.5" />
            <span>{skill.estimated_minutes_saved} דק'</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
            <Banknote className="w-3.5 h-3.5" />
            <span>₪{skill.estimated_cost_saved}</span>
          </div>
        </div>
        {activated ? (
          <button onClick={onManage}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors">
            <Settings2 className="w-3 h-3" />
            ניהול
          </button>
        ) : (
          <button onClick={onActivate}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-all duration-200">
            <Zap className="w-3 h-3" />
            הפעל
          </button>
        )}
      </div>
    </div>
  );
}
